import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

// Import modular components
import WhatsAppLayout from './WhatsAppLayout';
import WhatsAppHeader from './WhatsAppHeader';
import SendModal from './SendModal';

// Import utilities and hooks
import { extractPhoneNumber, groupMessagesByPhone } from '../utils/whatsappUtils';
import { useWhatsAppMessages } from '../../whatsapp2way/hooks/useWhatsAppMessages';

export default function WhatsAppDev() {
  const { currentAgent } = useAuth();
  const messagesEndRef = useRef(null);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const whatsappRole = currentAgent?.apps?.whatsapp?.role;
  const whatsappAllowed = currentAgent?.apps?.whatsapp?.allowed;

  // ============================================================================
  // HOOK USAGE
  // ============================================================================

  const {
    messages,
    groupedChats,
    searchTerm,
    setSearchTerm,
    isLoading,
    error,
    pageInfo,
    lastVisible,
    firstVisible,
    filterButtonMessages,
    setFilterButtonMessages,
    selectedContact,
    setSelectedContact,
    showSendModal,
    setShowSendModal,
    outgoingMessages,
    combinedMessages,
    unifiedContacts,
    filteredUnifiedContacts,
    loadMessages
  } = useWhatsAppMessages(whatsappAllowed);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const selectContact = (contactPhone) => {
    setSelectedContact(contactPhone);
  };

  const openSendModal = () => {
    setShowSendModal(true);
  };

  const closeSendModal = () => {
    setShowSendModal(false);
  };

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredContacts = Object.values(groupedChats).filter(contact => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();

    // Search in contact name
    if (contact.name.toLowerCase().includes(searchLower)) return true;

    // Search in contact phone
    if (contact.phone.toLowerCase().includes(searchLower)) return true;

    // Search in message bodies (properly extract from payload)
    return contact.messages.some(msg => {
      const messageInfo = extractMessageInfo(msg.payload);
      return messageInfo.body && messageInfo.body.toLowerCase().includes(searchLower);
    });
  });

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  if (!whatsappAllowed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <span className="text-2xl">🔒</span>
          </div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">
            Access Restricted
          </h2>
          <p className="text-red-600">
            You don't have access to the WhatsApp message reader.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4">

        <WhatsAppHeader
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          filterButtonMessages={filterButtonMessages}
          onFilterToggle={() => setFilterButtonMessages(prev => !prev)}
          isLoading={isLoading}
        />

        <WhatsAppLayout
          messages={messages}
          groupedChats={groupedChats}
          unifiedContacts={unifiedContacts}
          filteredContacts={filteredContacts}
          selectedContact={selectedContact}
          onSelectContact={selectContact}
          onSendMessage={openSendModal}
          whatsappRole={whatsappRole}
          error={error}
          isLoading={isLoading}
          pageInfo={pageInfo}
          onLoadPrev={() => loadMessages('prev')}
          onLoadNext={() => loadMessages('next')}
          lastVisible={lastVisible}
          firstVisible={firstVisible}
        />
      </div>

      {/* Send Message Modal */}
      <SendModal
        isOpen={showSendModal}
        onClose={closeSendModal}
        selectedContact={selectedContact}
        currentAgent={currentAgent}
      />
    </div>
  );
}
